import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import { alphabeticalSort } from 'utilities/sort';
import type { DropdownOption } from 'types/common/DropdownOption';
import {
  generateFilter,
  generateIncludeQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type { Workspace } from 'types/Workspace';
import type { WorkspaceUserResponse } from 'types/WorkspaceUser';
import { filterIncludedItem } from 'utilities/hooks/responseHandlers';
import { useCurrentUser } from 'hooks/common/useCurrentUser';

const getWorkspaces = (currentUserID: string) => {
  const queryArray = [
    generateIncludeQuery(['workspace']),
    generateFilter('userID', currentUserID),
  ];
  const url = generateURL(SPARK_RESOURCE_TYPES.WORKSPACE_USERS, queryArray);
  return fetchWrapper<WorkspaceUserResponse>({
    url: url,
    method: 'GET',
  });
};

export function useWorkspaces() {
  const { currentUserID } = useCurrentUser();
  const { data, ...rest } = useQuery(
    'workspaces',
    () => getWorkspaces(currentUserID || ''),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Workspaces: ${error.message}`);
        }
      },
      enabled: !!currentUserID,
      ...INFINITE_CACHE_AND_STALE_TIME,
    }
  );

  const { data: workspaceUsers, included, ...restData } = data || {};

  const workspaces = filterIncludedItem<Workspace>(
    included,
    SPARK_RESOURCE_TYPES.WORKSPACES
  );

  const idMap = workspaces?.reduce<Record<string, Workspace>>((acc, curr) => {
    return { ...acc, [curr.id]: curr };
  }, {});

  const sortedWorkspaces = workspaces?.sort((a, b) =>
    alphabeticalSort(a.attributes.name, b.attributes.name)
  );

  const dropdownOptions: DropdownOption[] =
    sortedWorkspaces?.map((workspace) => ({
      label: workspace.attributes.name,
      value: workspace.id,
      iconUrl: workspace.attributes.imageURL || '',
    })) || [];

  return {
    workspaces: sortedWorkspaces || [],
    workspaceUsers,
    idMap,
    dropdownOptions,
    ...restData,
    ...rest,
  };
}
